/**
 * Media URL Utilities
 * 
 * Handles URL normalization to ensure media URLs work across environments
 * (local development vs staging vs production)
 */

/**
 * Normalize a media URL to work on the current environment
 * Converts absolute localhost URLs to relative URLs so they work on any host
 */
export function normalizeMediaUrl(url: string | undefined | null): string {
  if (!url) return '';
  
  // Convert localhost URLs to relative
  if (url.startsWith('http://localhost:3001/')) {
    return url.replace('http://localhost:3001', '');
  }
  // Handle case without trailing slash (e.g. direct API calls)
  if (url.startsWith('http://localhost:3001')) {
    return url.replace('http://localhost:3001', '');
  }
  if (url.startsWith('http://localhost:3000/')) {
    return url.replace('http://localhost:3000', '');
  }
  if (url.startsWith('http://localhost:3000')) {
    return url.replace('http://localhost:3000', '');
  }
  if (url.startsWith('http://127.0.0.1:3001/')) {
    return url.replace('http://127.0.0.1:3001', '');
  }
  if (url.startsWith('http://127.0.0.1:3001')) {
    return url.replace('http://127.0.0.1:3001', '');
  }
  if (url.startsWith('http://127.0.0.1:3000/')) {
    return url.replace('http://127.0.0.1:3000', '');
  }
  
  return url;
}

/**
 * Check if a URL is a media proxy URL
 */
export function isProxyUrl(url: string): boolean {
  return url.includes('/api/media/proxy');
}

/**
 * Extract the file key from a proxy URL
 */
export function extractKeyFromProxyUrl(url: string): string | null {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.searchParams.get('key');
  } catch {
    // Try parsing as relative URL
    const match = url.match(/[?&]key=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
}

/**
 * Add userId to a proxy URL for authentication
 * This is needed because <img> tags can't send headers
 */
export function addUserIdToProxyUrl(url: string, userId: string): string {
  if (!url || !userId) return url;
  if (!isProxyUrl(url)) return url;
  
  // Check if userId is already in the URL
  if (url.includes('userId=')) return url;
  
  // Add userId as query parameter
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}userId=${encodeURIComponent(userId)}`;
}

/**
 * Ensure a media URL has authentication for the current user
 * Call this when rendering media URLs in the UI
 */
export async function getAuthenticatedMediaUrl(url: string): Promise<string> {
  if (!url) return url;
  if (!isProxyUrl(url)) return url;
  
  // Get current user ID from Supabase
  try {
    const { supabase } = await import('../lib/supabase');
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id) {
      return addUserIdToProxyUrl(url, user.id);
    }
  } catch (error) {
    console.warn('[mediaUrl] Failed to get user for URL auth:', error);
  }
  
  return url;
}

/**
 * Synchronous version - requires userId to be passed in
 */
export function getAuthenticatedMediaUrlSync(url: string, userId: string | null): string {
  if (!url || !userId) return url;
  return addUserIdToProxyUrl(url, userId);
}
